import { StyleSheet } from 'react-native';

import type { UiTheme } from "../theme";

// Shared styles for the read-only Program / Plan preview surfaces
// (lifted from apps/mobile in the Follow+Publish lift; mobile
// PlanPreview/PlanIntensityMatrix import from here too).
// (ProgramPreview, PlanPreview, PlanIntensityMatrix; the program grid
// itself lives in @jackedlogs/ui IntensityGrid). Density-second:
// big tap targets on the collapsible session headers, generous line
// height on the prescription lines — readability over compactness per
// the family's reduced-vision constraint.
export function makePreviewStyles(theme: UiTheme) {
  const { palette: p, spacing: s, radius: r, typography: t } = theme;
  return StyleSheet.create({
    container: {
      paddingTop: s.s2,
      gap: s.s3,
      borderTopWidth: StyleSheet.hairlineWidth,
      borderTopColor: p.border,
    },
    musclesRow: {
      gap: s.s2,
    },
    sectionLabel: {
      color: p.ink3,
      fontSize: t.sizes.xs,
      fontWeight: '700',
      letterSpacing: 0.6,
      textTransform: 'uppercase',
    },
    chipWrap: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: s.s2,
    },
    muscleChip: {
      paddingHorizontal: s.s3,
      paddingVertical: s.s1,
      borderRadius: r.r4,
      borderWidth: 1,
      borderColor: p.border,
      backgroundColor: p.surface,
    },
    muscleChipText: {
      color: p.ink2,
      fontSize: t.sizes.sm,
      fontWeight: '600',
    },
    // Collapsible session header — full-width tap target.
    sessionHeader: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: s.s2,
      paddingVertical: s.s3,
      minHeight: 44,
    },
    sessionHeaderPressed: { opacity: 0.6 },
    caret: {
      color: p.ink2,
      fontSize: t.sizes.sm,
      width: 16,
    },
    sessionName: {
      flex: 1,
      color: p.ink,
      fontSize: t.sizes.base,
      fontWeight: '700',
    },
    sessionBody: {
      gap: s.s2,
      paddingBottom: s.s2,
    },
    slotCard: {
      padding: s.s4,
      borderRadius: r.r3,
      borderWidth: 1,
      borderColor: p.border,
      backgroundColor: p.surface,
      gap: s.s2,
    },
    slotTitleRow: {
      flexDirection: 'row',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: s.s2,
    },
    slotName: {
      color: p.ink,
      fontSize: t.sizes.base,
      fontWeight: '700',
      letterSpacing: -0.2,
    },
    slotMuscles: {
      color: p.ink3,
      fontSize: t.sizes.sm,
      lineHeight: t.sizes.sm * 1.4,
    },
    slotNote: {
      color: p.ink3,
      fontSize: t.sizes.sm,
      lineHeight: t.sizes.sm * 1.4,
      fontStyle: 'italic',
    },
    divider: {
      height: StyleSheet.hairlineWidth,
      backgroundColor: p.border,
      marginVertical: s.s1,
    },
    weekRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: s.s3,
      minHeight: 28,
    },
    weekLabel: {
      color: p.ink,
      fontSize: t.sizes.sm,
      fontWeight: '700',
      fontVariant: ['tabular-nums'],
      minWidth: 44,
    },
    weekSummary: {
      flex: 1,
      color: p.ink2,
      fontSize: t.sizes.sm,
      lineHeight: t.sizes.sm * 1.4,
      fontVariant: ['tabular-nums'],
    },
    // Small badge pill for AMRAP / 1MAX cues.
    badge: {
      paddingHorizontal: s.s2,
      paddingVertical: 1,
      borderRadius: r.r4,
      borderWidth: 1,
      borderColor: p.ink3,
    },
    badgeText: {
      color: p.ink3,
      fontSize: t.sizes.xs,
      fontWeight: '700',
      letterSpacing: 0.4,
    },
    emptyText: {
      color: p.ink3,
      fontSize: t.sizes.sm,
      fontStyle: 'italic',
    },

    // ── Heat cells (shared by the plan matrix; the program grid's own
    // row/header styles live in @jackedlogs/ui gridStyles) ──
    gridCell: {
      flex: 1,
      height: 26,
      borderRadius: r.r2,
      alignItems: 'center',
      justifyContent: 'center',
    },
    // Band glyph (W/M/H/X) top-left — the non-colour cue so the band never
    // rides on fill colour alone (twin of gridStyles.gridCellBand; both heat
    // renderers carry it). `xs` full-opacity so the safety cue reads.
    gridCellBand: {
      position: 'absolute',
      top: 1,
      left: 3,
      fontSize: t.sizes.xs,
      fontWeight: '700',
    },
    // Prescribed set with no banded intensity (kg/history/free): outlined,
    // never flat-neutral — work must not read as an empty cell.
    gridCellFree: {
      backgroundColor: 'transparent',
      borderWidth: 1,
      borderColor: p.border,
    },
    gridCellNum: {
      fontSize: t.sizes.sm,
      fontWeight: '700',
      fontFamily: t.fonts.mono,
      fontVariant: ['tabular-nums'],
    },
    gridHint: {
      color: p.ink3,
      fontSize: t.sizes.xs,
      marginTop: s.s1,
    },
    // Spacing only — the swatch/glyph/label layout lives in <ScaleLegend> now.
    legendRow: { marginTop: s.s3 },

    // ── Plan intensity matrix (exercise × set heat) ──
    // A plan is one day, so the rows are EXERCISES (name on its own line for
    // reduced-vision readability) and the cells below are that exercise's
    // sets, coloured by band. The shared set-number header aligns because
    // every row is padded to setCols.
    matrixHeadRow: {
      flexDirection: 'row',
      gap: s.s1,
      marginBottom: s.s1,
    },
    matrixSetHead: {
      flex: 1,
      textAlign: 'center',
      color: p.ink3,
      fontSize: t.sizes.xs,
      fontVariant: ['tabular-nums'],
    },
    // Whole exercise block is one ≥44pt tap target — selection is per-row.
    // Baseline rows carry an invisible border with the selected ring's
    // metrics so selecting never shifts the layout.
    matrixExRow: {
      paddingVertical: s.s2,
      paddingHorizontal: s.s2,
      gap: s.s1,
      minHeight: 44,
      borderRadius: r.r2,
      borderWidth: 2,
      borderColor: 'transparent',
    },
    matrixExRowSelected: {
      // Selection = brand100 tint + neutral ring (not a brand-orange ring/name).
      // Colour budget S3.5.
      backgroundColor: p.brand100,
      borderColor: p.borderStrong,
    },
    matrixExRowPressed: { opacity: 0.6 },
    matrixExName: {
      color: p.ink2,
      fontSize: t.sizes.sm,
      fontWeight: '600',
    },
    matrixExNameSelected: { color: p.ink },
    matrixCells: {
      flexDirection: 'row',
      gap: s.s1,
    },

  });
}
